import { MiraSectionDocument } from "@/data/models/Mira";
import { caseCountIsValue } from "@/utils/MiraCaseUnits";
import {
  MIRA_COMPARISON_REFERENCE_ID,
  MiraComparisonView,
  ReferenceSpread,
  buildComparisonCore,
  headlineBasis,
  headlineValue,
  referenceSpread,
} from "@/utils/MiraComparisonView";
import { MiraViewContext } from "@/utils/MiraDataQualityView";

/** The id of the group's row, kept out of the client id space. */
export const MIRA_GROUP_VIEW_ID = "__group_view__";
/** The member's own bar beside the group's in the grouped bars. */
export const MIRA_GROUP_VIEW_OWN_ID = "__group_view_own__";

/**
 * Build a group member's comparison with its group for one figure (Hans, Track #2610112 X6d, decided by
 * Taimoor on 2026-10-28): the member's own value as the reference row and the group as the one row below,
 * drawn like the Markt ({@link buildMarketComparison} with one entity). The group's figures come from the
 * member's own `groupView_{section}` document, which IRES Cloud writes without any member id and with every
 * figure withheld that rests on fewer than three members, so no other member is named or can be worked out.
 * Every row carries its own P15 / median / P85 on one shared scale (`withSpread`, `VIEW_MIRA_PERCENTILES`).
 *
 * @param metricId the figure selected on the screen.
 * @param ownDocument the signed-in member's own document.
 * @param groupDocument the member's `groupView_{section}` document, or `null`.
 * @param groupLabel the group row's label ("Gruppe gesamt").
 * @param periodKey the chosen period.
 * @param comparisonPeriodKey the period the chosen one compares to (`undefined` for none).
 * @param formatValue the screen's formatter for this figure.
 * @param ctx translation and number formatting.
 * @param ownLabel the reference row's label (the member's own name).
 * @param withSpread whether to draw every row's own P15 / median / P85.
 * @param comparisonScale what the comparison period's value is multiplied by: a total against a longer baseline
 *   (`baselineScale`, 2026-11-02); 1 otherwise.
 */
export function buildGroupViewComparison(
  metricId: string,
  ownDocument: MiraSectionDocument | null | undefined,
  groupDocument: MiraSectionDocument | null | undefined,
  groupLabel: string,
  periodKey: string,
  comparisonPeriodKey: string | undefined,
  formatValue: (value: number) => string,
  ctx: MiraViewContext,
  ownLabel: string,
  withSpread = false,
  comparisonScale = 1
): MiraComparisonView {
  const noCases = caseCountIsValue(metricId);
  const view = buildComparisonCore(
    metricId,
    [
      {
        id: MIRA_GROUP_VIEW_ID,
        label: groupLabel,
        valueAt: (key) => headlineValue(groupDocument, key, metricId),
        basisAt: (key) => (noCases ? null : headlineBasis(groupDocument, key, metricId)),
      },
    ],
    (key) => headlineValue(ownDocument, key, metricId),
    (key) => (noCases ? null : headlineBasis(ownDocument, key, metricId)),
    ownLabel,
    periodKey,
    comparisonPeriodKey,
    formatValue,
    ctx,
    null,
    comparisonScale
  );

  // A group without the figure has it withheld for too few members behind it, or has none at all.
  view.withoutValueKey = "mira.comparison.groupView.withheld";
  // The group first, the member's own last (#2610137), as in the bars.
  view.referenceLast = true;

  // One scale for both bars and every row's markers, so no marker falls off its track.
  const spreads: Record<string, ReferenceSpread | null> = {
    [MIRA_COMPARISON_REFERENCE_ID]: withSpread ? referenceSpread(ownDocument, periodKey, metricId) : null,
    [MIRA_GROUP_VIEW_ID]: withSpread ? referenceSpread(groupDocument, periodKey, metricId) : null,
  };
  const shown = [...view.rows, ...(view.reference ? [view.reference] : [])];
  const largest = Math.max(
    0,
    ...shown.map((row) => Math.abs(row.value)),
    ...shown.map((row) => spreads[row.id]?.p85 ?? 0)
  );
  for (const row of shown) {
    row.fraction = largest > 0 ? Math.min(1, Math.abs(row.value) / largest) : 0;
    const spread = spreads[row.id];
    if (spread && largest > 0) {
      row.markers = {
        p15: spread.p15 / largest,
        median: spread.median / largest,
        p85: spread.p85 / largest,
        text: ctx.t("mira.comparison.markers", [formatValue(spread.p15), formatValue(spread.median), formatValue(spread.p85)]),
      };
    }
  }
  return view;
}
